/* global window document */
import { deepEqual as equal } from 'fast-equals';
import { throttle } from 'lodash';
import { useEffect, useState } from 'react';

/**
 * A representation of the windows dimensions (width x height)
 */
export interface WindowSize {
  /**
   * The width of the window in pixels
   */
  width: number;
  /**
   * The height of the window in pixels
   */
  height: number;
}

/**
 * Optional options
 */
export interface UseWindowSizeOptions {
  /**
   * The number of milliseconds to wait between force updates of the current component
   * @default 500
   */
  throttleRate?: number;
  /**
   * Whether to allow force updates of the current component on resize of the window
   * @default true
   */
  shouldUpdate?: boolean;
}

const isClient = typeof window === 'object';
function getWindowSize(s: WindowSize): WindowSize {
  const nextSize = {
    width: isClient ? document.documentElement.clientWidth : 0,
    height: isClient ? document.documentElement.clientHeight : 0
  };

  if (equal(nextSize, s)) {
    return s;
  }

  return nextSize;
}
/**
 * A hook that retrieves the width and height of the window and recalculates on resize
 */
const useWindowSize = ({
  throttleRate = 500,
  shouldUpdate = true
}: UseWindowSizeOptions = {}): WindowSize => {
  const [windowSize, setSize] = useState(() => getWindowSize({ width: 0, height: 0 }));

  useEffect(() => {
    if (!isClient || !shouldUpdate) {
      return;
    }

    let didCancel = false;

    const resizeListener = throttle(() => {
      if (!didCancel) {
        setSize(getWindowSize);
      }
    }, throttleRate);

    window.addEventListener('resize', resizeListener);

    return () => {
      didCancel = true;
      window.removeEventListener('resize', resizeListener);
    };
  });

  return windowSize;
};

export default useWindowSize;
